import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getEmployerApplications, updateApplicationStatus, APPLICATION_STATUS } from '../services/applicationService';
import { getEmployerJobs } from '../services/jobService';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';
import * as XLSX from 'xlsx';

const EmployerApplications = ({ user, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [experienceFilter, setExperienceFilter] = useState('any');
  const [skillsFilter, setSkillsFilter] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Helper to calculate total experience in years
  const calculateTotalExperience = (experience) => {
    if (!experience || !Array.isArray(experience)) return 0;

    return experience.reduce((acc, curr) => {
      // If explicit years provided
      if (curr.yearsOfExperience) return acc + (parseFloat(curr.yearsOfExperience) || 0);

      // Calculate from dates
      if (curr.startDate) {
        const start = new Date(curr.startDate);
        const end = curr.isCurrentJob || !curr.endDate ? new Date() : new Date(curr.endDate);
        const years = (end - start) / (1000 * 60 * 60 * 24 * 365.25);
        return acc + Math.max(0, years);
      }
      return acc;
    }, 0);
  };

  useEffect(() => {
    if (!user || userData?.userType !== 'employer') {
      navigate('/dashboard');
      return;
    }

    // Check if filter was passed from dashboard or manage jobs
    if (location.state?.filter) {
      console.log('📌 [EmployerApplications] Status filter received from navigation:', location.state.filter);
      setStatusFilter(location.state.filter);
    }

    // Check if job filter was passed from manage jobs
    if (location.state?.jobFilter) {
      console.log('📌 [EmployerApplications] Job filter received from navigation:', location.state.jobFilter);
      setJobFilter(location.state.jobFilter);
      setJobTitle(location.state.jobTitle || '');
    }

    fetchApplications();
    fetchJobs();
  }, [user, userData, navigate, location.state]);

  useEffect(() => {
    filterApplications();
  }, [applications, statusFilter, jobFilter, dateFilter, customStartDate, customEndDate, experienceFilter, skillsFilter, searchTerm]);

  const fetchJobs = async () => {
    if (!user) return;
    try {
      const result = await getEmployerJobs(user.uid);
      if (result.success) {
        setJobs(result.jobs);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchApplications = async () => {
    console.log('🔄 [EmployerApplications] Fetching applications for employer:', user.uid);
    setLoading(true);
    try {
      const result = await getEmployerApplications(user.uid);
      console.log('📊 [EmployerApplications] Fetch result:', result);

      if (result.success) {
        // Additional filter to ensure only current employer's applications
        const employerApplications = result.applications.filter(app => app.employerId === user.uid);
        console.log('✅ [EmployerApplications] Employer applications count:', employerApplications.length);
        setApplications(employerApplications);
      } else {
        console.error('❌ [EmployerApplications] Failed to fetch applications:', result.error);
        setApplications([]);
      }
    } catch (error) {
      console.error('❌ [EmployerApplications] Error fetching applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    // Filter by start date / date filter
    if (jobFilter !== 'all') {
      console.log('🔍 [EmployerApplications] Filtering by job:', jobFilter);
      filtered = filtered.filter(app => app.jobId === jobFilter);
    }

    // Filter by Experience
    if (experienceFilter !== 'any') {
      filtered = filtered.filter(app => {
        const totalExp = calculateTotalExperience(app.applicantProfile?.experience);
        if (experienceFilter === '0-1') return totalExp >= 0 && totalExp <= 1;
        if (experienceFilter === '1-3') return totalExp > 1 && totalExp <= 3;
        if (experienceFilter === '3-5') return totalExp > 3 && totalExp <= 5;
        if (experienceFilter === '5-10') return totalExp > 5 && totalExp <= 10;
        if (experienceFilter === '10+') return totalExp > 10;
        return true;
      });
    }

    // Filter by Skills
    if (skillsFilter) {
      const skillQuery = skillsFilter.toLowerCase();
      filtered = filtered.filter(app => {
        const skills = app.applicantProfile?.skills || [];
        return skills.some(s => s.toLowerCase().includes(skillQuery));
      });
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Filter by date
    const now = new Date();
    if (dateFilter === 'today') {
      filtered = filtered.filter(app => {
        if (!app.appliedAt) return false;
        const d = app.appliedAt;
        return d.getDate() === now.getDate() &&
          d.getMonth() === now.getMonth() &&
          d.getFullYear() === now.getFullYear();
      });
    } else if (dateFilter === 'last7') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - 7);
      filtered = filtered.filter(app => app.appliedAt && app.appliedAt >= cutoff);
    } else if (dateFilter === 'lastMonth') {
      const cutoff = new Date(now);
      cutoff.setMonth(cutoff.getMonth() - 1);
      filtered = filtered.filter(app => app.appliedAt && app.appliedAt >= cutoff);
    } else if (dateFilter === 'custom') {
      if (customStartDate) {
        const start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        filtered = filtered.filter(app => app.appliedAt && app.appliedAt >= start);
      }
      if (customEndDate) {
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        filtered = filtered.filter(app => app.appliedAt && app.appliedAt <= end);
      }
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(app =>
        app.applicantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.applicantEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    console.log('🔍 [EmployerApplications] Filtered applications:', filtered.length, 'of', applications.length);
    setFilteredApplications(filtered);
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    console.log('🔄 [EmployerApplications] Updating application status:', applicationId, newStatus);

    // Verify application ownership
    const application = applications.find(app => app.id === applicationId);
    if (!application || application.employerId !== user.uid) {
      alert('Error: You do not have permission to update this application.');
      console.error('❌ [EmployerApplications] Unauthorized status update attempt');
      return;
    }

    setUpdatingStatus(true);
    try {
      const result = await updateApplicationStatus(applicationId, newStatus, {
        updatedBy: user.uid
      });

      if (result.success) {
        setApplications(prev =>
          prev.map(app =>
            app.id === applicationId
              ? { ...app, status: newStatus, updatedAt: new Date() }
              : app
          )
        );
        setShowStatusModal(false);
        setSelectedApplication(null);
        console.log('✅ [EmployerApplications] Application status updated successfully');
      } else {
        alert('Error updating application status: ' + result.error);
        console.error('❌ [EmployerApplications] Status update failed:', result.error);
      }
    } catch (error) {
      alert('Error updating application status: ' + error.message);
      console.error('❌ [EmployerApplications] Status update error:', error);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleResumeView = async (applicationId) => {
    console.log('👁️ [EmployerApplications] Resume viewed for application:', applicationId);

    const application = applications.find(app => app.id === applicationId);
    if (application && application.status === APPLICATION_STATUS.PENDING) {
      // Auto-update status to 'reviewing' when resume is viewed
      await handleStatusUpdate(applicationId, APPLICATION_STATUS.REVIEWING);
    }
  };

  const getStatusCounts = () => {
    const counts = {
      all: applications.length,
      pending: applications.filter(app => app.status === APPLICATION_STATUS.PENDING).length,
      reviewing: applications.filter(app => app.status === APPLICATION_STATUS.REVIEWING).length,
      shortlisted: applications.filter(app => app.status === APPLICATION_STATUS.SHORTLISTED).length,
      selected: applications.filter(app => app.status === 'selected').length,
      rejected: applications.filter(app => app.status === APPLICATION_STATUS.REJECTED).length
    };
    return counts;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      [APPLICATION_STATUS.PENDING]: {
        bg: 'bg-gradient-to-r from-yellow-100 to-amber-100',
        text: 'text-yellow-800',
        label: 'Pending Review'
      },
      [APPLICATION_STATUS.REVIEWING]: {
        bg: 'bg-gradient-to-r from-blue-100 to-indigo-100',
        text: 'text-blue-800',
        label: 'Viewed'
      },
      [APPLICATION_STATUS.SHORTLISTED]: {
        bg: 'bg-gradient-to-r from-purple-100 to-pink-100',
        text: 'text-purple-800',
        label: 'Shortlisted'
      },
      ['selected']: {
        bg: 'bg-gradient-to-r from-green-100 to-emerald-100',
        text: 'text-green-800',
        label: 'Selected'
      },
      [APPLICATION_STATUS.REJECTED]: {
        bg: 'bg-gradient-to-r from-red-100 to-rose-100',
        text: 'text-red-800',
        label: 'Rejected'
      }
    };

    const config = statusConfig[status] || statusConfig[APPLICATION_STATUS.PENDING];
    return (
      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${config.bg} ${config.text} border border-opacity-20`}>
        {config.label}
      </span>
    );
  };

  const getStatusProgress = (status) => {
    const statusOrder = {
      [APPLICATION_STATUS.PENDING]: 1,
      [APPLICATION_STATUS.REVIEWING]: 2,
      [APPLICATION_STATUS.SHORTLISTED]: 3,
      ['selected']: 4,
      [APPLICATION_STATUS.REJECTED]: 5
    };
    return statusOrder[status] || 1;
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      [APPLICATION_STATUS.PENDING]: APPLICATION_STATUS.REVIEWING,
      [APPLICATION_STATUS.REVIEWING]: APPLICATION_STATUS.SHORTLISTED,
      [APPLICATION_STATUS.SHORTLISTED]: 'selected',
      ['selected']: APPLICATION_STATUS.REJECTED
    };
    return statusFlow[currentStatus];
  };

  const handleCardClick = (filter) => {
    console.log('📊 [EmployerApplications] Card clicked with filter:', filter);
    setStatusFilter(filter);
  };

  const handleJobFilterChange = (e) => {
    const jobId = e.target.value;
    setJobFilter(jobId);
    if (jobId === 'all') {
      setJobTitle('');
    } else {
      const selectedJob = jobs.find(j => j.id === jobId);
      if (selectedJob) setJobTitle(selectedJob.title);
    }
  };

  const exportToExcel = () => {
    console.log('📄 [EmployerApplications] Exporting to Excel, filter:', statusFilter);

    if (filteredApplications.length === 0) {
      alert('No applications to export. Please adjust your filters.');
      return;
    }

    try {
      // Prepare data for Excel export
      const exportData = filteredApplications.map((app, index) => {
        const skills = app.applicantProfile?.skills ? app.applicantProfile.skills.join(', ') : 'N/A';
        const experience = app.applicantProfile?.experience ?
          app.applicantProfile.experience.map(exp =>
            `${exp.jobTitle} at ${exp.company} (${exp.startDate} - ${exp.isCurrentJob ? 'Present' : exp.endDate})`
          ).join('; ') : 'N/A';
        const education = app.applicantProfile?.education ?
          app.applicantProfile.education.map(edu =>
            `${edu.degree} in ${edu.fieldOfStudy} from ${edu.institution} (${edu.graduationYear})`
          ).join('; ') : 'N/A';

        return {
          'S.No': index + 1,
          'Applicant Name': app.applicantName || 'N/A',
          'Email': app.applicantEmail || 'N/A',
          'Phone': app.applicantPhone || 'N/A',
          'Location': app.applicantLocation || 'N/A',
          'Job Title': app.jobTitle || 'N/A',
          'Company': app.companyName || 'N/A',
          'Application Status': app.status || 'N/A',
          'Applied Date': app.appliedAt ? app.appliedAt.toLocaleDateString() : 'N/A',
          'Last Updated': app.updatedAt ? app.updatedAt.toLocaleDateString() : 'N/A',
          'Skills': skills,
          'Professional Summary': app.applicantProfile?.professionalSummary || 'N/A',
          'Cover Letter': app.coverLetter || 'N/A',
          'Expected Salary (Min)': app.applicantProfile?.expectedSalary?.min || 'N/A',
          'Expected Salary (Max)': app.applicantProfile?.expectedSalary?.max || 'N/A',
          'Currency': app.applicantProfile?.expectedSalary?.currency || 'N/A',
          'Notice Period': app.applicantProfile?.noticePeriod || 'N/A',
          'Work Experience': experience,
          'Education': education,
          'Resume URL': app.resumeURL || 'N/A',
          'Application Source': app.applicationSource || 'N/A'
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 8 },   // S.No
        { wch: 20 },  // Applicant Name
        { wch: 25 },  // Email
        { wch: 15 },  // Phone
        { wch: 15 },  // Location
        { wch: 20 },  // Job Title
        { wch: 20 },  // Company
        { wch: 15 },  // Status
        { wch: 12 },  // Applied Date
        { wch: 12 },  // Last Updated
        { wch: 30 },  // Skills
        { wch: 40 },  // Professional Summary
        { wch: 40 },  // Cover Letter
        { wch: 12 },  // Expected Salary Min
        { wch: 12 },  // Expected Salary Max
        { wch: 10 },  // Currency
        { wch: 15 },  // Notice Period
        { wch: 50 },  // Work Experience
        { wch: 50 },  // Education
        { wch: 30 },  // Resume URL
        { wch: 15 }   // Application Source
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      const filterText = statusFilter === 'all' ? 'All_Applications' :
        statusFilter === APPLICATION_STATUS.PENDING ? 'Pending_Applications' :
          statusFilter === APPLICATION_STATUS.REVIEWING ? 'Viewed_Applications' :
            statusFilter === APPLICATION_STATUS.SHORTLISTED ? 'Shortlisted_Applications' :
              statusFilter === 'selected' ? 'Selected_Applications' :
                statusFilter === APPLICATION_STATUS.REJECTED ? 'Rejected_Applications' : 'Applications';

      XLSX.utils.book_append_sheet(wb, ws, filterText);

      // Generate filename with current date and filter
      const currentDate = new Date().toISOString().split('T')[0];
      const dateLabel = dateFilter !== 'all' ? `_${dateFilter}` : '';
      const filename = `Job_Applications_${filterText}${dateLabel}_${currentDate}.xlsx`;

      // Save the file
      XLSX.writeFile(wb, filename);

      console.log('✅ [EmployerApplications] Excel export completed:', filename);

      // Show success message
      alert(`Successfully exported ${filteredApplications.length} applications to ${filename}`);

    } catch (error) {
      console.error('❌ [EmployerApplications] Excel export error:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const statusCounts = getStatusCounts();

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {jobFilter !== 'all' && jobTitle ? `Applications for "${jobTitle}"` : 'Manage Applications'}
              </h1>
              <p className="mt-2 text-gray-600">
                {jobFilter !== 'all' && jobTitle
                  ? `Viewing applications specifically for the "${jobTitle}" position.`
                  : 'View and manage all job applications in one place.'
                }
              </p>
              {jobFilter !== 'all' && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    Filtered by Job
                  </span>
                  <button
                    onClick={() => {
                      setJobFilter('all');
                      setJobTitle('');
                    }}
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                  >
                    Clear Job Filter
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportToExcel}
                disabled={filteredApplications.length === 0}
                className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${filteredApplications.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export to Excel
                {filteredApplications.length > 0 && (
                  <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
                    {filteredApplications.length}
                  </span>
                )}
              </button>
              <Link
                to="/manage-jobs"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                View Jobs
              </Link>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Cards - Matching Employer Dashboard Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <button
            onClick={() => handleCardClick('all')}
            className={`bg-gradient-to-br from-slate-500 to-gray-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full ${statusFilter === 'all' ? 'ring-4 ring-gray-300 ring-opacity-50' : ''
              }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-100 text-sm font-medium uppercase tracking-wide">All Applications</p>
                <p className="text-4xl font-bold mt-2">{statusCounts.all}</p>
                <div className="flex items-center mt-2">
                  <span className="text-gray-200 text-xs">All submissions • Click to view</span>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleCardClick(APPLICATION_STATUS.REVIEWING)}
            className={`bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full ${statusFilter === APPLICATION_STATUS.REVIEWING ? 'ring-4 ring-blue-300 ring-opacity-50' : ''
              }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium uppercase tracking-wide">Viewed</p>
                <p className="text-4xl font-bold mt-2">{statusCounts.reviewing}</p>
                <div className="flex items-center mt-2">
                  <span className="text-blue-200 text-xs">Resume reviewed • Click to view</span>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleCardClick(APPLICATION_STATUS.SHORTLISTED)}
            className={`bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full ${statusFilter === APPLICATION_STATUS.SHORTLISTED ? 'ring-4 ring-purple-300 ring-opacity-50' : ''
              }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium uppercase tracking-wide">Shortlisted</p>
                <p className="text-4xl font-bold mt-2">{statusCounts.shortlisted}</p>
                <div className="flex items-center mt-2">
                  <span className="text-purple-200 text-xs">Top candidates • Click to view</span>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleCardClick('selected')}
            className={`bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full ${statusFilter === 'selected' ? 'ring-4 ring-emerald-300 ring-opacity-50' : ''
              }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium uppercase tracking-wide">Selected</p>
                <p className="text-4xl font-bold mt-2">{statusCounts.selected}</p>
                <div className="flex items-center mt-2">
                  <span className="text-emerald-200 text-xs">Final selection • Click to view</span>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleCardClick(APPLICATION_STATUS.REJECTED)}
            className={`bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl cursor-pointer text-left w-full ${statusFilter === APPLICATION_STATUS.REJECTED ? 'ring-4 ring-red-300 ring-opacity-50' : ''
              }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium uppercase tracking-wide">Rejected</p>
                <p className="text-4xl font-bold mt-2">{statusCounts.rejected}</p>
                <div className="flex items-center mt-2">
                  <span className="text-red-200 text-xs">Not suitable • Click to view</span>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-2xl p-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="space-y-4">
            {/* Date Filter Row */}
            <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-gray-100">
              <span className="text-sm font-bold text-gray-500 uppercase tracking-wide mr-1">📅 Date:</span>
              {[
                { key: 'all', label: 'All Time', bg: 'bg-gray-600', ring: 'ring-gray-400' },
                { key: 'today', label: 'Today', bg: 'bg-sky-500', ring: 'ring-sky-300' },
                { key: 'last7', label: 'Last 7 Days', bg: 'bg-violet-500', ring: 'ring-violet-300' },
                { key: 'lastMonth', label: 'Last Month', bg: 'bg-orange-500', ring: 'ring-orange-300' },
                { key: 'custom', label: '📆 Custom', bg: 'bg-rose-500', ring: 'ring-rose-300' },
              ].map(btn => (
                <button
                  key={btn.key}
                  onClick={() => setDateFilter(btn.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold text-white transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 ${dateFilter === btn.key
                    ? `${btn.bg} ring-2 ${btn.ring} scale-105 shadow-md`
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    }`}
                >
                  {btn.label}
                </button>
              ))}

              {/* Custom Date Inputs */}
              {dateFilter === 'custom' && (
                <div className="flex flex-wrap items-center gap-3 pl-2 border-l border-gray-200 ml-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-gray-600">From:</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={e => setCustomStartDate(e.target.value)}
                      className="px-3 py-1.5 border border-rose-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-gray-600">To:</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={e => setCustomEndDate(e.target.value)}
                      className="px-3 py-1.5 border border-rose-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Advanced Filters: Experience & Skills */}
            <div className="flex flex-col md:flex-row gap-4 pb-4 border-b border-gray-100">
              <div className="md:w-64">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level
                </label>
                <select
                  value={experienceFilter}
                  onChange={(e) => setExperienceFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="any">Any Experience</option>
                  <option value="0-1">0 - 1 Years</option>
                  <option value="1-3">1 - 3 Years</option>
                  <option value="3-5">3 - 5 Years</option>
                  <option value="5-10">5 - 10 Years</option>
                  <option value="10+">10+ Years</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Skills
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="e.g. React, Java, Marketing..."
                    value={skillsFilter}
                    onChange={(e) => setSkillsFilter(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Search, Job & Status Filters */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="md:w-64">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Job
                </label>
                <select
                  value={jobFilter}
                  onChange={handleJobFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="all">All Jobs</option>
                  {jobs.map(job => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Applications
                </label>
                <input
                  type="text"
                  placeholder="Search by name, email, job title, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="md:w-48">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status ({statusCounts.all})</option>
                  <option value={APPLICATION_STATUS.PENDING}>Pending Review ({statusCounts.pending})</option>
                  <option value={APPLICATION_STATUS.REVIEWING}>Viewed ({statusCounts.reviewing})</option>
                  <option value={APPLICATION_STATUS.SHORTLISTED}>Shortlisted ({statusCounts.shortlisted})</option>
                  <option value="selected">Selected ({statusCounts.selected})</option>
                  <option value={APPLICATION_STATUS.REJECTED}>Rejected ({statusCounts.rejected})</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Applications ({filteredApplications.length})
                {statusFilter !== 'all' && (
                  <span className="ml-2 text-sm text-gray-500">
                    - Filtered by: {statusFilter === APPLICATION_STATUS.PENDING ? 'Pending Review' :
                      statusFilter === APPLICATION_STATUS.REVIEWING ? 'Viewed' :
                        statusFilter === APPLICATION_STATUS.SHORTLISTED ? 'Shortlisted' :
                          statusFilter === 'selected' ? 'Selected' :
                            statusFilter === APPLICATION_STATUS.REJECTED ? 'Rejected' : statusFilter}
                  </span>
                )}
              </h2>
              {filteredApplications.length > 0 && (
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 text-sm shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export ({filteredApplications.length})
                </button>
              )}
            </div>
          </div>
          <div className="p-6">
            {filteredApplications.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {applications.length === 0 ? 'No applications yet' : 'No applications match your filters'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {applications.length === 0
                    ? 'Applications will appear here once candidates start applying to your jobs.'
                    : 'Try adjusting your search or filter criteria.'
                  }
                </p>
                {applications.length > 0 && (
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchTerm('');
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {filteredApplications.map((application) => (
                  <div key={application.id} className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                      {/* Left Section - Applicant Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                              {application.applicantName?.charAt(0)?.toUpperCase() || 'A'}
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900 mb-1">{application.applicantName}</h3>
                              <p className="text-base text-gray-600 mb-1">
                                Applied for: <span className="font-semibold text-indigo-600">{application.jobTitle}</span>
                              </p>
                              {application.companyName && (
                                <p className="text-sm text-gray-500">
                                  Company: <span className="font-medium text-gray-700">{application.companyName}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getStatusBadge(application.status)}
                            <span className="text-xs text-gray-500">
                              Applied {application.appliedAt?.toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {/* Contact Info */}
                        <div className="bg-gray-50 rounded-xl p-4 mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-3 p-2 bg-white rounded-lg">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <span className="text-gray-700">{application.applicantEmail}</span>
                            </div>
                            <div className="flex items-center gap-3 p-2 bg-white rounded-lg">
                              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                              </div>
                              <span className="text-gray-700">{application.applicantPhone}</span>
                            </div>
                            <div className="flex items-center gap-3 p-2 bg-white rounded-lg">
                              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                </svg>
                              </div>
                              <span className="text-gray-700">{application.applicantLocation}</span>
                            </div>
                          </div>
                        </div>

                        {/* Skills Preview */}
                        {application.applicantProfile?.skills && application.applicantProfile.skills.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Key Skills</h4>
                            <div className="flex flex-wrap gap-2">
                              {application.applicantProfile.skills.slice(0, 6).map((skill, index) => (
                                <span key={index} className="px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-xs font-medium shadow-sm hover:shadow-md transition-shadow">
                                  {skill}
                                </span>
                              ))}
                              {application.applicantProfile.skills.length > 6 && (
                                <span className="px-3 py-2 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-lg text-xs font-medium shadow-sm">
                                  +{application.applicantProfile.skills.length - 6} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Additional Info */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {application.applicantProfile?.expectedSalary && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                              <div className="text-green-600 font-semibold text-sm">Expected Salary</div>
                              <div className="text-green-800 font-bold text-xs mt-1">
                                {application.applicantProfile.expectedSalary.currency} {application.applicantProfile.expectedSalary.min}-{application.applicantProfile.expectedSalary.max}
                              </div>
                            </div>
                          )}
                          {application.applicantProfile?.noticePeriod && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                              <div className="text-orange-600 font-semibold text-sm">Notice Period</div>
                              <div className="text-orange-800 font-bold text-xs mt-1">
                                {application.applicantProfile.noticePeriod}
                              </div>
                            </div>
                          )}
                          {application.applicantProfile?.experience && application.applicantProfile.experience.length > 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                              <div className="text-blue-600 font-semibold text-sm">Experience</div>
                              <div className="text-blue-800 font-bold text-xs mt-1">
                                {application.applicantProfile.experience.length} position(s)
                              </div>
                            </div>
                          )}
                          {application.applicantProfile?.education && application.applicantProfile.education.length > 0 && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                              <div className="text-purple-600 font-semibold text-sm">Education</div>
                              <div className="text-purple-800 font-bold text-xs mt-1">
                                {application.applicantProfile.education.length} degree(s)
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right Section - Actions */}
                      <div className="flex flex-col gap-4 min-w-[220px]">
                        {/* Status Progress Bar */}
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                          <div className="text-sm font-semibold text-gray-700 mb-3">Application Progress</div>
                          <div className="flex items-center gap-1 mb-2">
                            {[1, 2, 3, 4, 5].map((step) => (
                              <div
                                key={step}
                                className={`h-3 flex-1 rounded-full transition-all duration-300 ${step <= getStatusProgress(application.status)
                                  ? step === 5 ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-md' : step === 4 ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-md' : 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-md'
                                  : 'bg-gray-300'
                                  }`}
                              />
                            ))}
                          </div>
                          <div className="text-xs text-gray-600 font-medium">
                            Step {getStatusProgress(application.status)} of 5 - {getStatusBadge(application.status).props.children}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowDetailsModal(true);
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            View Details
                          </div>
                        </button>

                        {application.resumeURL && (
                          <a
                            href={application.resumeURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleResumeView(application.id)}
                            className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all duration-200 text-center text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                          >
                            <div className="flex items-center justify-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              View Resume
                            </div>
                            {application.status === APPLICATION_STATUS.PENDING && (
                              <span className="block text-xs opacity-90 mt-1">Auto-updates to Viewed</span>
                            )}
                          </a>
                        )}

                        {/* Quick Status Actions */}
                        <div className="grid grid-cols-2 gap-2">
                          {application.status !== APPLICATION_STATUS.SHORTLISTED && application.status !== 'selected' && application.status !== APPLICATION_STATUS.REJECTED && (
                            <button
                              onClick={() => handleStatusUpdate(application.id, APPLICATION_STATUS.SHORTLISTED)}
                              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 text-xs shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                              Shortlist
                            </button>
                          )}

                          {application.status === APPLICATION_STATUS.SHORTLISTED && (
                            <button
                              onClick={() => handleStatusUpdate(application.id, 'selected')}
                              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 text-xs shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                              Select
                            </button>
                          )}

                          {application.status !== APPLICATION_STATUS.REJECTED && (
                            <button
                              onClick={() => handleStatusUpdate(application.id, APPLICATION_STATUS.REJECTED)}
                              className="px-4 py-2 bg-gradient-to-r from-red-500 to-rose-500 text-white font-medium rounded-lg hover:from-red-600 hover:to-rose-600 transition-all duration-200 text-xs shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                              Reject
                            </button>
                          )}
                        </div>

                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowStatusModal(true);
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-medium rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Change Status
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Back to Dashboard */}
        <div className="mt-8 text-center">
          <Link
            to="/dashboard"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to Dashboard
          </Link>
        </div>

        {/* Enhanced Details Modal */}
        {showDetailsModal && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 overflow-y-auto backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 text-white">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center text-white font-bold text-2xl">
                      {selectedApplication.applicantName?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold">{selectedApplication.applicantName}</h3>
                      <p className="text-indigo-100 text-lg mt-1">Applied for: {selectedApplication.jobTitle}</p>
                      <div className="mt-2">{getStatusBadge(selectedApplication.status)}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-white hover:text-gray-200 bg-white bg-opacity-20 rounded-full p-2 hover:bg-opacity-30 transition-all"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-8 overflow-y-auto max-h-[calc(95vh-140px)]">

                {/* Contact Information */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 mb-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-gray-500 text-sm">Email Address</span>
                          <p className="font-semibold text-gray-900">{selectedApplication.applicantEmail}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-gray-500 text-sm">Phone Number</span>
                          <p className="font-semibold text-gray-900">{selectedApplication.applicantPhone}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-gray-500 text-sm">Location</span>
                          <p className="font-semibold text-gray-900">{selectedApplication.applicantLocation}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2" />
                          </svg>
                        </div>
                        <div>
                          <span className="text-gray-500 text-sm">Applied Date</span>
                          <p className="font-semibold text-gray-900">{selectedApplication.appliedAt?.toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Professional Summary */}
                {selectedApplication.applicantProfile?.professionalSummary && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 mb-8">
                    <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Professional Summary
                    </h4>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-gray-700 leading-relaxed">{selectedApplication.applicantProfile.professionalSummary}</p>
                    </div>
                  </div>
                )}

                {/* Cover Letter */}
                {selectedApplication.coverLetter && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-8">
                    <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Cover Letter
                    </h4>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-gray-700 leading-relaxed">{selectedApplication.coverLetter}</p>
                    </div>
                  </div>
                )}

                {/* Skills */}
                {selectedApplication.applicantProfile?.skills && selectedApplication.applicantProfile.skills.length > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 mb-8">
                    <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      Technical Skills
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {selectedApplication.applicantProfile.skills.map((skill, index) => (
                        <span key={index} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-shadow">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Experience */}
                {selectedApplication.applicantProfile?.experience && selectedApplication.applicantProfile.experience.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Work Experience</h4>
                    <div className="space-y-4">
                      {selectedApplication.applicantProfile.experience.map((exp, index) => (
                        <div key={index} className="border-l-4 border-blue-500 pl-4">
                          <h5 className="font-medium text-gray-900">{exp.jobTitle}</h5>
                          <p className="text-sm text-gray-600">{exp.company} • {exp.location}</p>
                          <p className="text-sm text-gray-600">
                            {exp.startDate} - {exp.isCurrentJob ? 'Present' : exp.endDate} • {exp.employmentType}
                          </p>
                          {exp.description && <p className="text-sm text-gray-700 mt-2">{exp.description}</p>}
                          {exp.achievements && <p className="text-sm text-gray-700 mt-1"><strong>Achievements:</strong> {exp.achievements}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {selectedApplication.applicantProfile?.education && selectedApplication.applicantProfile.education.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Education</h4>
                    <div className="space-y-4">
                      {selectedApplication.applicantProfile.education.map((edu, index) => (
                        <div key={index} className="border-l-4 border-green-500 pl-4">
                          <h5 className="font-medium text-gray-900">{edu.degree} in {edu.fieldOfStudy}</h5>
                          <p className="text-sm text-gray-600">{edu.institution} • {edu.location}</p>
                          <p className="text-sm text-gray-600">Graduated: {edu.graduationYear}</p>
                          {edu.grade && <p className="text-sm text-gray-700">Grade: {edu.grade}</p>}
                          {edu.description && <p className="text-sm text-gray-700 mt-1">{edu.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expected Salary & Notice Period */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {selectedApplication.applicantProfile?.expectedSalary && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Expected Salary</h4>
                      <p className="text-gray-700">
                        {selectedApplication.applicantProfile.expectedSalary.currency} {selectedApplication.applicantProfile.expectedSalary.min} - {selectedApplication.applicantProfile.expectedSalary.max}
                      </p>
                    </div>
                  )}
                  {selectedApplication.applicantProfile?.noticePeriod && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Notice Period</h4>
                      <p className="text-gray-700">{selectedApplication.applicantProfile.noticePeriod}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="bg-gray-50 rounded-2xl p-6 mt-8">
                  <div className="flex flex-wrap gap-4 justify-center">
                    {selectedApplication.resumeURL && (
                      <a
                        href={selectedApplication.resumeURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleResumeView(selectedApplication.id)}
                        className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Resume
                      </a>
                    )}
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setShowStatusModal(true);
                      }}
                      className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Update Status
                    </button>
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="px-8 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {showStatusModal && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Update Application Status
              </h3>
              <p className="text-gray-600 mb-4">
                Updating status for: <strong>{selectedApplication.applicantName}</strong>
              </p>

              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  <strong>Status Flow:</strong> Pending → Viewed → Shortlisted → Selected → Rejected
                </div>

                {[
                  APPLICATION_STATUS.PENDING,
                  APPLICATION_STATUS.REVIEWING,
                  APPLICATION_STATUS.SHORTLISTED,
                  'selected',
                  APPLICATION_STATUS.REJECTED
                ].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(selectedApplication.id, status)}
                    disabled={updatingStatus}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-200 ${selectedApplication.status === status
                      ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-300 shadow-md'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      } ${updatingStatus ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
                  >
                    <div className="flex items-center justify-between">
                      {getStatusBadge(status)}
                      {selectedApplication.status === status && (
                        <span className="text-indigo-600 font-medium text-sm">Current</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    if (!showDetailsModal) {
                      setSelectedApplication(null);
                    }
                  }}
                  disabled={updatingStatus}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default EmployerApplications;
